import { FC, forwardRef } from 'react';
import { Box, FormLabel, Typography } from '@mui/material';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import styles from '../CustomReactDatePicker/CustomReactDatePicker.module.scss';
import { DateIcon } from '../../components/Icons';

type CustomTimePickerProps = {
  required?: boolean;
  disabled?: boolean;
  name?: string;
  inputLabel?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  customClass?: string;
  minTime?: string;
  maxTime?: string;
  hideIcon?: boolean;
  iconPosition?: 'left' | 'right';
  timeDisplayFormat?: string;
};

type CustomInputProps = {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  hideIcon?: boolean;
  iconPosition?: 'left' | 'right';
};

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onClick, placeholder, error, disabled, hideIcon, iconPosition = 'left' }, ref) => {
    const icon = !hideIcon ? (
      <Box
        className={styles.iconWrapper}
        onClick={onClick}
      >
        <DateIcon size={24} />
      </Box>
    ) : null;

    return (
      <Box className={`${styles.inputWrapper} ${iconPosition === 'right' ? styles.iconRight : ''}`}>
        {iconPosition === 'left' ? icon : null}
        <input
          ref={ref}
          value={value}
          onClick={onClick}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          className={`${styles.customInput} ${error ? styles.error : ''}`}
        />
        {iconPosition === 'right' ? icon : null}
      </Box>
    );
  }
);

const CustomTimePicker: FC<CustomTimePickerProps> = ({
  required = false,
  disabled = false,
  name = '',
  inputLabel = 'Time',
  placeholder = 'Select time',
  value = '',
  error = false,
  helperText = '',
  onChange,
  customClass,
  minTime,
  maxTime,
  hideIcon = false,
  iconPosition = 'left',
  timeDisplayFormat = 'HH:mm',
}) => {
  const timeValue = value ? new Date(`1970-01-01T${value}:00`) : null;

  const hasTimeBounds = Boolean(minTime || maxTime);
  const minTimeDate = hasTimeBounds
    ? new Date(`1970-01-01T${minTime ?? '00:00'}:00`)
    : undefined;
  const maxTimeDate = hasTimeBounds
    ? new Date(`1970-01-01T${maxTime ?? '23:59'}:00`)
    : undefined;

  const handleTimeChange = (time: Date | null) => {
    if (onChange) {
      const timeString = time
        ? time.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : '';

      const syntheticEvent = {
        target: {
          name,
          value: timeString,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
  };

  return (
    <Box className={`${styles.customDatePickerContainer} ${customClass}`}>
      <FormLabel className={styles.customDatePickerLabel}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>

      <DatePicker
        selected={timeValue}
        onChange={handleTimeChange}
        disabled={disabled}
        minTime={minTimeDate}
        maxTime={maxTimeDate}
        placeholderText={placeholder}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={5}
        timeCaption="Time"
        dateFormat={timeDisplayFormat}
        timeFormat={timeDisplayFormat}
        showPopperArrow={false}
        popperClassName={styles.customPopper}
        calendarClassName={styles.customCalendar}
        customInput={
          <CustomInput
            error={error}
            disabled={disabled}
            placeholder={placeholder}
            hideIcon={hideIcon}
            iconPosition={iconPosition}
          />
        }
      />

      {helperText && error && (
        <Typography
          variant="h11"
          className={styles.errorMessage}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default CustomTimePicker;
